import { useEffect, useState } from 'react';
import { Mail, MapPin, Megaphone, Phone } from 'lucide-react';
import { patientPortal } from '@/api/patientPortal';
import PatientPortalPageHero from './PatientPortalPageHero';
import { SUPPORT_EMAIL, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '@/constants/patientPortalContact';

type Hospital = {
  id: string;
  name: string;
  address: string;
  contactNumber: string;
  email: string;
  mapsLink: string;
};

export default function PatientPortalContact() {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [profileName, setProfileName] = useState('');

  useEffect(() => {
    patientPortal.getMe().then((p: { name?: string }) => {
      if (p?.name) setProfileName(p.name);
    }).catch(() => {});
    patientPortal
      .getContactHospitals()
      .then((data: { hospitals?: Hospital[] }) => {
        setHospitals(Array.isArray(data?.hospitals) ? data.hospitals : []);
      })
      .catch(() => setHospitals([]))
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    setMsg('');
    const trimmed = query.trim();
    if (trimmed.length < 10) {
      setMsg('Please enter at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await patientPortal.submitContactQuery(trimmed);
      setMsg('Thank you. Our team will respond shortly.');
      setQuery('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setMsg(err.response?.data?.message ?? 'Could not send your message.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-xl">
      <PatientPortalPageHero
        imageKey="home"
        eyebrow="Support"
        title="Contact us"
        subtitle={
          profileName
            ? `Reach True Hospitals — signed in as ${profileName}`
            : 'Reach True Hospitals for appointments, billing, or general help'
        }
        compact
      />

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Get in touch</h2>
        <a
          href={`tel:${SUPPORT_PHONE_TEL}`}
          className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 hover:bg-slate-50 transition"
        >
          <Phone className="h-5 w-5 text-teal-700 shrink-0" />
          <div>
            <p className="text-xs text-slate-500">Phone</p>
            <p className="font-medium text-slate-900">{SUPPORT_PHONE_DISPLAY}</p>
          </div>
        </a>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 hover:bg-slate-50 transition"
        >
          <Mail className="h-5 w-5 text-teal-700 shrink-0" />
          <div>
            <p className="text-xs text-slate-500">Email</p>
            <p className="font-medium text-slate-900">{SUPPORT_EMAIL}</p>
          </div>
        </a>
      </section>

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-amber-600" />
          Send a query
        </h2>
        <p className="text-sm text-slate-600">
          Your message is emailed to our care team. Include your UHID or visit details if relevant.
        </p>
        <textarea
          className="w-full min-h-[120px] border border-slate-200 rounded-lg px-3 py-2 text-sm"
          placeholder="Type your question or feedback…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={4000}
        />
        {msg ? (
          <p className={`text-sm ${msg.startsWith('Thank') ? 'text-teal-800' : 'text-red-600'}`}>{msg}</p>
        ) : null}
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="w-full rounded-xl bg-teal-800 text-white py-3 text-sm font-semibold hover:bg-teal-900 disabled:opacity-60"
        >
          {submitting ? 'Sending…' : 'Submit query'}
        </button>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Our hospitals</h2>
        {loading ? (
          <p className="text-sm text-slate-500">Loading locations…</p>
        ) : hospitals.length === 0 ? (
          <p className="text-sm text-slate-500">Locations are not available right now.</p>
        ) : (
          hospitals.map((h) => (
            <article
              key={h.id}
              className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80 space-y-2"
            >
              <h3 className="font-semibold text-slate-900">{h.name}</h3>
              <p className="text-sm text-slate-600 flex gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-teal-700 mt-0.5" />
                {h.address}
              </p>
              {h.contactNumber ? (
                <p className="text-sm text-slate-600">
                  <a href={`tel:${h.contactNumber.replace(/\s/g, '')}`} className="text-teal-800 hover:underline">
                    {h.contactNumber}
                  </a>
                </p>
              ) : null}
              {h.email ? (
                <p className="text-sm">
                  <a href={`mailto:${h.email}`} className="text-teal-800 hover:underline">
                    {h.email}
                  </a>
                </p>
              ) : null}
              <a
                href={h.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full rounded-lg bg-teal-800 text-white py-2.5 text-sm font-medium hover:bg-teal-900"
              >
                Open in Google Maps
              </a>
            </article>
          ))
        )}
      </section>
    </div>
  );
}
